import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAcceptTerms, useDeclineAndClose, useMeProfile } from '../hooks/useApi';
import { cognitoHelpers } from '../auth/cognito';
import { useAuth } from '../auth/AuthContext';
import type { IdentityType } from '../types';

type Step = 'identity' | 'agreement';

export function TermsPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { data, isLoading, isError, error, refetch } = useMeProfile();
  const acceptMutation = useAcceptTerms();
  const declineMutation = useDeclineAndClose();

  const [step, setStep] = useState<Step>('identity');
  const [identityType, setIdentityType] = useState<IdentityType | ''>('');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [registeredAddress, setRegisteredAddress] = useState('');
  const [role, setRole] = useState('');
  const [country, setCountry] = useState('');
  const [confirmIdentity, setConfirmIdentity] = useState(false);
  const [acceptAgreement, setAcceptAgreement] = useState(false);
  const [confirmAuthority, setConfirmAuthority] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);

  useEffect(() => {
    if (!data) return;
    if (data.identityType) setIdentityType(data.identityType);
    setGivenName(data.givenName ?? '');
    setFamilyName(data.familyName ?? '');
    setCompanyName(data.companyName ?? '');
    setRegistrationNumber(data.registrationNumber ?? '');
    setRegisteredAddress(data.registeredAddress ?? '');
    setRole(data.role ?? '');
    setCountry(data.country ?? '');
  }, [data]);

  const email = data?.email ?? '';
  const termsUrl = data?.termsOfUseUrl?.trim() ?? '';
  const termsVersion = data?.termsVersion ?? '';
  const termsHash = data?.termsHash ?? '';

  const displayName = useMemo(() => {
    if (identityType === 'company') return companyName.trim();
    return `${givenName.trim()} ${familyName.trim()}`.trim();
  }, [identityType, companyName, givenName, familyName]);

  const identityComplete = useMemo(() => {
    if (!confirmIdentity) return false;
    if (!givenName.trim() || !familyName.trim()) return false;
    if (identityType === 'company') {
      return Boolean(
        companyName.trim() && registrationNumber.trim() && registeredAddress.trim() && role.trim()
      );
    }
    if (identityType === 'individual') {
      return Boolean(country.trim());
    }
    return false;
  }, [
    identityType,
    confirmIdentity,
    givenName,
    familyName,
    companyName,
    registrationNumber,
    registeredAddress,
    role,
    country,
  ]);

  const handleConfirmIdentity = (e: FormEvent) => {
    e.preventDefault();
    if (!identityComplete) return;
    setStep('agreement');
  };

  const handleAccept = async () => {
    if (!identityType || !acceptAgreement || !confirmAuthority) return;
    try {
      await acceptMutation.mutateAsync({
        identityType,
        givenName: givenName.trim(),
        familyName: familyName.trim(),
        companyName: identityType === 'company' ? companyName.trim() : undefined,
        registrationNumber: identityType === 'company' ? registrationNumber.trim() : undefined,
        registeredAddress: identityType === 'company' ? registeredAddress.trim() : undefined,
        role: identityType === 'company' ? role.trim() : undefined,
        country: identityType === 'individual' ? country.trim() : undefined,
        confirmIdentity: true,
        acceptAgreement: true,
        confirmAuthority: true,
        termsVersion,
        termsHash,
      });
      const refreshed = await cognitoHelpers.refreshTokens();
      if (!refreshed) {
        navigate('/login', { replace: true });
        return;
      }
      await refetch();
      navigate('/', { replace: true });
    } catch {
      // mutation surfaces error state
    }
  };

  const handleDecline = async () => {
    try {
      await declineMutation.mutateAsync();
      logout();
    } catch {
      // mutation surfaces error state
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" px={2}>
        <Alert severity="error" onClose={() => refetch()}>
          {error instanceof Error ? error.message : 'Failed to load profile.'}
        </Alert>
      </Box>
    );
  }

  if (data?.termsAcceptedAt?.trim()) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 4, bgcolor: 'grey.50' }}>
      <Container maxWidth="sm">
        <Paper elevation={2} sx={{ p: 4 }}>
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Welcome. Before proceeding, please verify the information associated with your account.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please verify the information associated with your account.
            </Typography>
          </Stack>

          {acceptMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {acceptMutation.error instanceof Error
                ? acceptMutation.error.message
                : 'Could not save. Please try again.'}
            </Alert>
          )}
          {declineMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {declineMutation.error instanceof Error
                ? declineMutation.error.message
                : 'Could not close the account. Please try again.'}
            </Alert>
          )}

          {step === 'identity' && (
            <Box component="form" onSubmit={handleConfirmIdentity}>
              <Stack spacing={3}>
                <FormControl>
                  <FormLabel>I am accessing the Software:</FormLabel>
                  <RadioGroup
                    value={identityType}
                    onChange={(e) => setIdentityType(e.target.value as IdentityType)}
                  >
                    <FormControlLabel
                      value="company"
                      control={<Radio />}
                      label="on behalf of a company or organization (as its authorized representative)"
                    />
                    <FormControlLabel
                      value="individual"
                      control={<Radio />}
                      label="in my own name, in the course of my professional activity"
                    />
                  </RadioGroup>
                </FormControl>

                {identityType === 'company' && (
                  <Stack spacing={2}>
                    <TextField
                      label="Company name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      required
                      fullWidth
                    />
                    <TextField
                      label="Registration number"
                      value={registrationNumber}
                      onChange={(e) => setRegistrationNumber(e.target.value)}
                      required
                      fullWidth
                    />
                    <TextField
                      label="Registered address"
                      value={registeredAddress}
                      onChange={(e) => setRegisteredAddress(e.target.value)}
                      required
                      fullWidth
                      multiline
                      minRows={2}
                    />
                    <TextField
                      label="Last name"
                      value={familyName}
                      onChange={(e) => setFamilyName(e.target.value)}
                      required
                      fullWidth
                      autoComplete="family-name"
                    />
                    <TextField
                      label="First name"
                      value={givenName}
                      onChange={(e) => setGivenName(e.target.value)}
                      required
                      fullWidth
                      autoComplete="given-name"
                    />
                    <TextField
                      label="Your role within the company"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      required
                      fullWidth
                    />
                    <TextField
                      label="Professional email"
                      value={email}
                      disabled
                      fullWidth
                      helperText="Verified via your account"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={confirmIdentity}
                          onChange={(e) => setConfirmIdentity(e.target.checked)}
                        />
                      }
                      label="I confirm the above information is accurate and that I have authority to bind the above-named entity."
                    />
                  </Stack>
                )}

                {identityType === 'individual' && (
                  <Stack spacing={2}>
                    <TextField
                      label="Last name"
                      value={familyName}
                      onChange={(e) => setFamilyName(e.target.value)}
                      required
                      fullWidth
                      autoComplete="family-name"
                    />
                    <TextField
                      label="First name"
                      value={givenName}
                      onChange={(e) => setGivenName(e.target.value)}
                      required
                      fullWidth
                      autoComplete="given-name"
                    />
                    <TextField
                      label="Professional email"
                      value={email}
                      disabled
                      fullWidth
                      helperText="Verified via your account"
                    />
                    <TextField
                      label="Country of activity"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      required
                      fullWidth
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={confirmIdentity}
                          onChange={(e) => setConfirmIdentity(e.target.checked)}
                        />
                      }
                      label="I confirm the above information is accurate and that I am accessing the Software in the course of my professional activity and not as a consumer."
                    />
                  </Stack>
                )}

                <Button type="submit" variant="contained" disabled={!identityComplete}>
                  Confirm
                </Button>
              </Stack>
            </Box>
          )}

          {step === 'agreement' && (
            <Stack spacing={3}>
              <Typography variant="h5">Beta Testing Agreement</Typography>
              <Typography variant="body1">
                You are about to bind <strong>{displayName || '—'}</strong> under the Beta Testing
                Agreement.
              </Typography>

              {termsUrl ? (
                <Typography variant="body2">
                  <Link href={termsUrl} target="_blank" rel="noopener noreferrer">
                    Read the Beta Testing Agreement{' '}
                    <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                  </Link>
                </Typography>
              ) : (
                <Alert severity="warning">Terms of use URL is not configured.</Alert>
              )}

              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptAgreement}
                      onChange={(e) => setAcceptAgreement(e.target.checked)}
                    />
                  }
                  label={`I have read, understood, and unreservedly accept the Beta Testing Agreement dated ${termsVersion || '—'}.`}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmAuthority}
                      onChange={(e) => setConfirmAuthority(e.target.checked)}
                    />
                  }
                  label={`I hereby represent and warrant that I have the authority to bind ${displayName || '—'} under this Beta Testing Agreement.`}
                />
              </Stack>

              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    !acceptAgreement ||
                    !confirmAuthority ||
                    !termsUrl ||
                    !termsVersion ||
                    !termsHash ||
                    acceptMutation.isPending
                  }
                  onClick={handleAccept}
                >
                  {acceptMutation.isPending ? 'Saving…' : 'Accept'}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={declineMutation.isPending}
                  onClick={() => setDeclineDialogOpen(true)}
                >
                  Decline and close my account
                </Button>
                <Button variant="text" onClick={() => setStep('identity')}>
                  Back
                </Button>
              </Stack>
            </Stack>
          )}
        </Paper>
      </Container>

      <Dialog open={declineDialogOpen} onClose={() => setDeclineDialogOpen(false)}>
        <DialogTitle>Close your account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Declining will disable your account and revoke your API access. You can ask an
            administrator to re-enable it. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={declineMutation.isPending}
            onClick={async () => {
              setDeclineDialogOpen(false);
              await handleDecline();
            }}
          >
            {declineMutation.isPending ? 'Closing…' : 'Decline and close the account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
